import type { RefObject } from 'react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { SlideOut } from '~/chords';
import type { SlideOutRef } from '~/chords/SlideOut/types';
import { Icon, Separator } from '~/components';
import { Button } from '~/components/Button';
import { Input } from '~/components/Input';
import { TextArea } from '~/components/TextArea';
import {
  deleteTrackOverwrite,
  getTrackOverwrite,
  upsertTrackOverwrite,
} from '~/modules/label-perf/api/overwrites';

export interface TrackOverwriteProps {
  isrc: string;
  className?: string;
  onClose?: () => void;
  panelRef: RefObject<SlideOutRef>;
}

interface FormData {
  trackTitle?: string;
  artistName?: string;
  comment?: string;
}

interface Authorship {
  createdBy: string;
  createdAt: Date;
  updatedBy: string;
  updatedAt: Date;
}

export function TrackOverwrite(props: TrackOverwriteProps) {
  const { t } = useTranslation('label-perf');
  const [formData, setFormData] = useState<FormData | null>(null);
  const [authorship, setAuthorship] = useState<Authorship | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reload, setReload] = useState<string>('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!props.isrc) {
      return;
    }

    getTrackOverwrite(props.isrc)
      .then((data) => {
        if (data) {
          setFormData({
            trackTitle: data.track_title,
            artistName: data.artist_name,
            comment: data.comment,
          });
          setAuthorship({
            createdBy: data.created_by.name,
            createdAt: new Date(data.created_at),
            updatedBy: data.updated_by.name,
            updatedAt: new Date(data.updated_at),
          });
        }

        setLoading(false);

        return false;
      })
      .catch((_error) => {
        setError(
          `Failed to fetch track overwrite data: ${(_error as Error).message}`,
        );
        setLoading(false);
      });
  }, [props.isrc, reload]);

  const handleInputChange =
    (field: keyof FormData): ((value: string) => void) =>
    (value: string): void => {
      setFormData((prev) => ({ ...prev, [field]: value }));
    };

  const handleOnSave = async (): Promise<void> => {
    setSaving(true);
    setError(null);

    try {
      await upsertTrackOverwrite({
        isrc: props.isrc,
        track_title: formData?.trackTitle,
        artist_name: formData?.artistName,
        comment: formData?.comment,
      });
      setReload(new Date().toISOString());
    } catch (_error) {
      setError(
        `Failed to save track overwrite data: ${(_error as Error).message}`,
      );
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (): Promise<void> => {
    setAuthorship(null);
    setSaving(true);
    setError(null);

    try {
      await deleteTrackOverwrite(props.isrc);
    } catch (_error) {
      setError(
        `Failed to delete track overwrite data: ${(_error as Error).message}`,
      );
    } finally {
      setSaving(false);
      props.onClose?.();
    }
  };

  return (
    <SlideOut ref={props.panelRef} panelWidth="560px">
      <SlideOut.Header>{t('body.overwriteIsrc')}</SlideOut.Header>
      <SlideOut.Body>
        <div className="flex min-h-0 grow flex-col">
          <div className="mb-md">
            <p className="text-body-md font-semibold text-default">
              {t('body.isrc')}:{' '}
              <span className="text-subtle">{props.isrc}</span>
            </p>
          </div>
          <Separator />

          {loading ? (
            <div className="my-md" data-testid="initial-loading">
              <Icon
                type="spinner"
                variant="solid"
                size="sm"
                className="fa-spin mb-sm"
              />
            </div>
          ) : null}
          {!loading && (
            <>
              <div className="my-md">
                <p className="mb-sm text-body-sm font-semibold text-default">
                  {t('body.trackTitle')}
                </p>
                <Input
                  readOnly={loading}
                  onChange={(e) =>
                    handleInputChange('trackTitle')(e.target.value)
                  }
                  placeholder="Track Title, empty value means no overwrrite"
                  value={formData?.trackTitle || ''}
                  className="w-full rounded-sm"
                />
              </div>
              <div className="mb-md">
                <p className="mb-sm text-body-sm font-semibold text-default">
                  {t('body.artistName')}
                </p>
                <Input
                  readOnly={loading}
                  onChange={(e) =>
                    handleInputChange('artistName')(e.target.value)
                  }
                  placeholder="Artist Name, empty value means no overwrrite"
                  value={formData?.artistName || ''}
                  className="w-full rounded-sm"
                />
              </div>
              <div className="mb-md">
                <p className="mb-sm text-body-sm font-semibold text-default">
                  {t('body.comment')}
                </p>
                <TextArea
                  readOnly={loading}
                  onChange={(e) => handleInputChange('comment')(e.target.value)}
                  placeholder="Describe why overwrite is done"
                  value={formData?.comment || ''}
                  className="w-full rounded-sm"
                />
              </div>
            </>
          )}
          {!loading && authorship ? (
            <div className="mb-sm text-body-sm text-subtle">
              <div>
                {t('body.createdBy')} {authorship.createdBy} {t('body.at')}{' '}
                {authorship.createdAt.toLocaleDateString()}{' '}
                {authorship.createdAt.toLocaleTimeString()}
              </div>
              <div>
                {t('table.modifiedBy')} {authorship.updatedBy} {t('body.at')}{' '}
                {authorship.updatedAt.toLocaleDateString()}{' '}
                {authorship.updatedAt.toLocaleTimeString()}
              </div>
            </div>
          ) : null}
          {error ? (
            <div className="mb-sm text-body-sm text-error-default">
              <p>{error}</p>
            </div>
          ) : null}

          <div className="my-sm text-body-sm text-subtle">
            <p className="mb-xs font-semibold text-default">
              {t('body.notes')}
            </p>
            <p className="mb-sm">{t('desc.overwrite')}</p>
            <p className="mb-sm">{t('desc.overwritenData')}</p>
            <p className="mb-sm">{t('desc.overwriteTakeEffect')}</p>
            <p className="mb-sm">{t('desc.provideOverwrite')}</p>
          </div>
        </div>
      </SlideOut.Body>
      <SlideOut.Footer>
        {authorship ? (
          <Button
            size="large"
            variant="tertiary"
            onClick={handleDelete}
            disabled={loading || saving}
          >
            {t('actions.delete')}
          </Button>
        ) : null}
        <Button
          size="large"
          variant="primary"
          onClick={handleOnSave}
          disabled={loading || saving}
        >
          {saving ? t('actions.saving') : t('actions.save')}
        </Button>
      </SlideOut.Footer>
    </SlideOut>
  );
}

TrackOverwrite.displayName = 'TrackOverwrite';
